import { Box, TextFieldProps, useTheme } from '@mui/material';
import { Button, Flex, IconButton, Input, Text } from '@components';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CategorizedData,
  NomineeCustomer,
} from '@features/api/response/NomineeCustomer';
import { useForm } from 'react-hook-form';
import { nomineeCustomerFormSchema } from '@validationSchemas/nominee';
import { yupResolver } from '@hookform/resolvers/yup';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { useCreateNomineeCustomerMutation } from '@features/api/nomineeCustomerSlice';
import { ChangeEvent, useState } from 'react';
import {
  CategoryContainer,
  CategoryHeaderContainer,
} from './NewCustomerForm.styles';
import { InputProps } from '@components/Input/Input';
import NewItemDialog from './NewItemDialog';

export interface NewCustomerFormProps {
  onSave: () => void;
}

function StyledInput({
  removeable = false,
  handleRemove,
  ...restProps
}: InputProps &
  TextFieldProps & { removeable?: boolean; handleRemove?: () => void }) {
  return (
    <Input
      {...restProps}
      InputProps={{
        sx: {
          flex: 1,
        },
        endAdornment: removeable ? (
          <IconButton
            onClick={handleRemove}
            size='small'
            iconBoxHeight={12}
            iconBoxWidth={12}
            icon={() => <DeleteOutlineIcon fontSize='small' color='error' />}
          />
        ) : null,
      }}
      sx={{
        flex: '0 49%',
      }}
    />
  );
}

export const NewCustomerForm = ({ onSave }: NewCustomerFormProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const [createMutation] = useCreateNomineeCustomerMutation();

  const {
    register,
    reset,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<Omit<NomineeCustomer, 'id'>>({
    resolver: yupResolver(nomineeCustomerFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
  });

  const [newCategoryName, setNewCategoryName] = useState<string>();
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const [showFieldForm, setShowFieldForm] = useState(false);
  const [newFieldName, setNewFieldName] = useState<string>();
  const [newFieldCategoryIndex, setNewFieldCategoryIndex] = useState<number>();

  const [categorizedData, setCategorizedData] = useState<CategorizedData[]>([]);

  const handleSave = async (data: Omit<NomineeCustomer, 'id'>) => {
    await createMutation({
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      categorizedData: categorizedData,
    }).unwrap();

    onSave();
    reset();
  };

  const handleRemoveCategory = (index: number) => {
    if (categorizedData && index < categorizedData.length) {
      const updatedCategories = [...categorizedData];
      updatedCategories.splice(index, 1);

      setCategorizedData([...updatedCategories]);
    }
  };

  const handleRemoveField = (categoryIndex: number, fieldIndex: number) => {
    if (categorizedData && categoryIndex < categorizedData.length) {
      const updatedCategories = [...categorizedData];
      const updatedCategory = updatedCategories[categoryIndex];
      updatedCategory.fields.splice(fieldIndex, 1);

      setCategorizedData([...updatedCategories]);
    }
  };

  const handleChangeField = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    categoryIndex: number,
    fieldIndex: number
  ) => {
    if (categorizedData && categoryIndex < categorizedData.length) {
      const updatedCategories = [...categorizedData];
      const updatedCategory = updatedCategories[categoryIndex];
      updatedCategory.fields[fieldIndex] = {
        ...updatedCategory.fields[fieldIndex],
        value: event.target.value,
      };

      setCategorizedData([...updatedCategories]);
    }
  };

  const handleAddCategory = () => {
    setShowCategoryForm(true);
    setNewCategoryName(undefined);
  };

  const handleCancelCreateCategory = () => {
    setShowCategoryForm(false);
    setNewCategoryName(undefined);
  };

  const handleCreateCategory = () => {
    if (!newCategoryName) return;

    setCategorizedData([
      ...categorizedData,
      { name: newCategoryName, fields: [] },
    ]);
    setShowCategoryForm(false);
  };

  const handleAddField = (categoryIndex: number) => {
    setShowFieldForm(true);
    setNewFieldName(undefined);
    setNewFieldCategoryIndex(categoryIndex);
  };

  const handleCancelAddField = () => {
    setShowFieldForm(false);
    setNewFieldName(undefined);
    setNewFieldCategoryIndex(undefined);
  };

  const handleCreateField = () => {
    if (!newFieldName) return;

    if (
      categorizedData &&
      newFieldCategoryIndex !== undefined &&
      newFieldCategoryIndex < categorizedData.length
    ) {
      const updatedCategories = [...categorizedData];

      if (updatedCategories[newFieldCategoryIndex]?.fields) {
        updatedCategories[newFieldCategoryIndex]?.fields.push({
          name: newFieldName,
          value: '',
        });
      } else {
        updatedCategories[newFieldCategoryIndex].fields = [
          {
            name: newFieldName,
            value: '',
          },
        ];
      }

      setCategorizedData([...updatedCategories]);
      setShowFieldForm(false);
      setNewFieldCategoryIndex(undefined);
    }
  };

  const handleNewCategoryNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setNewCategoryName(event.target.value);
  };

  const handleNewFieldNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewFieldName(event.target.value);
  };

  return (
    <Flex flex={1} flexDirection='column' justifyContent='space-between'>
      <Flex flex={1} flexDirection='column' gap={10}>
        <form onSubmit={handleSubmit(handleSave)}>
          <CategoryContainer>
            <Text variant='h5' fontWeight='fontWeightMedium'>
              <FormattedMessage id='crm.customer.basicDetails.personalDetails' />
            </Text>

            <Flex flexDirection='row' flexWrap='wrap' gap={6}>
              <StyledInput
                label={intl.formatMessage({ id: 'crm.firstName' })}
                {...registerInput(register, 'firstName')}
                {...getErrorProps(intl, errors.firstName)}
              />
              <StyledInput
                label={intl.formatMessage({ id: 'crm.middleName' })}
                {...registerInput(register, 'middleName')}
                {...getErrorProps(intl, errors.middleName)}
              />
              <StyledInput
                label={intl.formatMessage({ id: 'crm.lastName' })}
                {...registerInput(register, 'lastName')}
                {...getErrorProps(intl, errors.lastName)}
              />
            </Flex>
          </CategoryContainer>
        </form>
        {categorizedData?.map((data, index) => (
          <CategoryContainer>
            <CategoryHeaderContainer>
              <Text variant='h5' fontWeight='fontWeightMedium'>
                {data.name}
              </Text>
              <IconButton
                onClick={() => handleRemoveCategory(index)}
                size='small'
                iconBoxHeight={12}
                icon={() => (
                  <DeleteOutlineIcon sx={{ color: theme.palette.error.main }} />
                )}
              />
            </CategoryHeaderContainer>
            <Flex flexWrap='wrap' gap={6}>
              {data.fields.map((field, fieldIndex) => (
                <StyledInput
                  key={field.name}
                  label={field.name}
                  defaultValue={field.value}
                  onChange={(event) =>
                    handleChangeField(event, index, fieldIndex)
                  }
                  removeable
                  handleRemove={() => handleRemoveField(index, fieldIndex)}
                />
              ))}
            </Flex>

            <Flex flex={1} alignItems='center' justifyContent='center'>
              <Button variant='tertiary' onClick={() => handleAddField(index)}>
                <FormattedMessage id='crm.customer.addField' />
              </Button>
            </Flex>
          </CategoryContainer>
        ))}
        <Flex alignItems='flex-start'>
          <Flex
            justifySelf='flex-start'
            flex={1}
            alignItems='center'
            justifyContent='center'
          >
            <Button variant='secondary' onClick={handleAddCategory}>
              <FormattedMessage id='crm.customer.addCategory' />
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Box mt={14}>
        <Button
          data-testid='save-button'
          variant='primary'
          type='submit'
          onClick={handleSubmit(handleSave)}
          disabled={!isValid}
        >
          <FormattedMessage id='misc.save' />
        </Button>
      </Box>

      <NewItemDialog
        isOpen={showCategoryForm}
        onClose={handleCancelCreateCategory}
        label={intl.formatMessage({ id: 'crm.customer.categoryName' })}
        value={newCategoryName}
        onChangeValue={handleNewCategoryNameChange}
        onSubmit={handleCreateCategory}
        onCancel={handleCancelCreateCategory}
      />

      <NewItemDialog
        isOpen={showFieldForm}
        onClose={handleCancelAddField}
        label={intl.formatMessage({ id: 'crm.customer.fieldLabel' })}
        value={newFieldName}
        onChangeValue={handleNewFieldNameChange}
        onSubmit={handleCreateField}
        onCancel={handleCancelAddField}
      />
    </Flex>
  );
};
